import { Container, Graphics, InteractionEvent, Rectangle } from "pixi.js"

import { createIconButton } from "../button_factory"
import { UiButton } from "../ui_button"
import { ACCENT, RIM, TEXT } from "./theme"

// Turning the character on the forge's stage. The figure stands on a turntable: drag anywhere on the
// stage and it turns with the pointer (the near side follows it, as a model on a stand would), or
// step it one view at a time with the buttons either side of its feet. A ring on the floor marks
// which way it faces, with a notch for every view its body draws -- eight for an Ember body, four
// for an LPC paperdoll.

const TAU = Math.PI * 2

/** How far a drag travels for one whole turn: about the width of the stage. */
export const TURN_PX = 240
/** How quickly the ring's marker catches up with the figure. */
const GLIDE_MS = 60
/** How quickly the ring brightens under the pointer, and fades again. */
const EMPHASIS_MS = 140

export const wrapAngle = (angle: number): number => ((angle % TAU) + TAU) % TAU

const shortestTurn = (from: number, to: number): number => {
    const delta = wrapAngle(to - from)
    return delta > Math.PI ? delta - TAU : delta
}

const unitVector = (x: number, y: number) => {
    const length = Math.hypot(x, y) || 1
    return { x: x / length, y: y / length }
}

/** How many views a body draws, judged by how it picks a facing for an angle. */
export const viewCountOf = (calcDir?: (angle: number) => string): number => {
    if (!calcDir) {
        return 4
    }
    const facings = new Set<string>()
    for (let k = 0; k < 8; k++) {
        facings.add(calcDir((k * TAU) / 8))
    }
    return facings.size >= 8 ? 8 : 4
}

/** The view nearest an angle. */
export const nearestView = (angle: number, views: number): number => {
    const unit = TAU / views
    return wrapAngle(Math.round(angle / unit) * unit)
}

/**
 * One view round from wherever the figure stands. `direction` 1 swings its near side to the right,
 * the way the right-hand button and a drag to the right both do: a figure facing the viewer turns to
 * face right.
 */
export const turnedBy = (angle: number, views: number, direction: number): number =>
    nearestView(nearestView(angle, views) - (direction * TAU) / views, views)

/** Where a drag of `dx` px leaves a figure that faced `startAngle` when it was taken hold of. */
export const angleAfterDrag = (startAngle: number, dx: number): number => wrapAngle(startAngle - (dx * TAU) / TURN_PX)

export interface TurntableLayout {
    /** The ring's centre: where the figure stands. */
    x: number
    y: number
    /** The ring's radii -- wide and flat, a circle on the floor seen from above and in front. */
    rx: number
    ry: number
}

export class Turntable {
    /** The way the figure faces, as its body is told: continuous during a drag, a view otherwise. */
    angle: number
    /** How many views the body on the stand draws. */
    views = 4
    /** The player has turned the figure, so the hint inviting them to has done its job. */
    turned = false
    /** The floor ring. It outlives repaints, like the figure it sits under. */
    readonly ring = new Graphics()

    // Where the ring's marker is drawn: it glides after `angle` rather than jumping with the figure.
    private shown: number
    private emphasis = 0
    private hovered = false
    private drag?: { pointerId: number; startX: number; startAngle: number }
    private drawnKey = ""

    constructor(
        private layout: TurntableLayout,
        angle: number,
        private onTurn: (angle: number) => void,
    ) {
        this.angle = wrapAngle(angle)
        this.shown = this.angle
        this.draw()
    }

    get dragging(): boolean {
        return !!this.drag
    }

    /** One view round; see turnedBy. */
    turn = (direction: number) => {
        this.drag = undefined
        this.face(turnedBy(this.angle, this.views, direction))
    }

    /**
     * The stage's drag catcher for this repaint: transparent, over the figure and under the buttons.
     * The drag itself lives here rather than on the catcher, which is thrown away by every repaint.
     */
    surface(area: Rectangle): Container {
        const surface = new Container()
        surface.hitArea = area
        surface.interactive = true
        surface.cursor = this.drag ? "grabbing" : "grab"

        surface.on("pointerover", () => (this.hovered = true))
        surface.on("pointerout", () => (this.hovered = false))
        surface.on("pointerdown", (event: InteractionEvent) => {
            event?.stopPropagation?.()
            const { data } = event
            if (data.pointerType !== "touch" && (data.button ?? 0) > 0) {
                return
            }
            this.drag = { pointerId: data.pointerId, startX: data.getLocalPosition(surface).x, startAngle: this.angle }
            surface.cursor = "grabbing"
        })
        // pixi hands pointermove to every interactive object, hit or not, so the drag keeps its moves
        // when the pointer strays off the stage.
        surface.on("pointermove", (event: InteractionEvent) => {
            const drag = this.drag
            if (!drag || event.data.pointerId !== drag.pointerId) {
                return
            }
            this.face(angleAfterDrag(drag.startAngle, event.data.getLocalPosition(surface).x - drag.startX))
        })
        const release = (event: InteractionEvent) => {
            if (!this.drag || event?.data?.pointerId !== this.drag.pointerId) {
                return
            }
            this.endDrag()
            surface.cursor = "grab"
        }
        for (const name of ["pointerup", "pointerupoutside", "pointercancel"]) {
            surface.on(name, release)
        }
        return surface
    }

    /** Let go: the figure settles on the view it is showing. */
    endDrag = () => {
        if (!this.drag) {
            return
        }
        this.drag = undefined
        this.face(nearestView(this.angle, this.views))
    }

    /** The catcher is about to be thrown away, and pixi never tells a removed object the pointer left. */
    detach = () => {
        this.endDrag()
        this.hovered = false
    }

    update(dtMs: number) {
        const gap = shortestTurn(this.shown, this.angle)
        this.shown = Math.abs(gap) < 0.002 ? this.angle : wrapAngle(this.shown + gap * (1 - Math.exp(-dtMs / GLIDE_MS)))
        const target = this.drag ? 1 : this.hovered ? 0.55 : 0
        const step = dtMs / EMPHASIS_MS
        this.emphasis =
            this.emphasis < target ? Math.min(target, this.emphasis + step) : Math.max(target, this.emphasis - step)
        this.draw()
    }

    private face(angle: number) {
        if (nearestView(angle, this.views) !== nearestView(this.angle, this.views)) {
            this.turned = true
        }
        this.angle = angle
        this.onTurn(angle)
    }

    private draw() {
        const key = `${this.shown.toFixed(3)}|${this.emphasis.toFixed(2)}|${this.views}`
        if (key === this.drawnKey) {
            return
        }
        this.drawnKey = key

        const { x, y, rx, ry } = this.layout
        const e = this.emphasis
        const g = this.ring
        g.clear()
        const point = (a: number, reach = 1) => ({ x: x + rx * reach * Math.cos(a), y: y + ry * reach * Math.sin(a) })
        // One path per stroke: overlapping segments at a lower alpha would leave bright seams.
        const arc = (from: number, to: number) => {
            const steps = Math.max(4, Math.ceil(Math.abs(to - from) / 0.1))
            for (let i = 0; i <= steps; i++) {
                const p = point(from + ((to - from) * i) / steps)
                if (i === 0) {
                    g.moveTo(p.x, p.y)
                } else {
                    g.lineTo(p.x, p.y)
                }
            }
        }

        // The rim of the stand. Its near half is the brighter, so it reads as lying on the floor; the
        // far half runs behind the figure's legs.
        g.lineStyle(1, RIM, 0.22 + 0.3 * e)
        arc(0, Math.PI)
        g.lineStyle(1, RIM, 0.1 + 0.18 * e)
        arc(Math.PI, TAU)
        g.lineStyle(0)

        // A notch for every view the body has, so it is plain there is more to see than this one.
        for (let i = 0; i < this.views; i++) {
            const a = (i * TAU) / this.views
            const p = point(a)
            g.beginFill(RIM, (Math.sin(a) < -0.01 ? 0.3 : 0.55) + 0.35 * e)
            g.drawCircle(p.x, p.y, 1.7)
            g.endFill()
        }

        // The way the figure faces: a lit stretch of rim with a point off its middle. The point is a
        // fixed size off the rim's edge, so it neither shrinks to nothing at the front nor reaches
        // into the buttons at the sides.
        const a = this.shown
        const half = 0.3
        g.lineStyle(6, ACCENT, 0.1 + 0.12 * e)
        arc(a - half, a + half)
        g.lineStyle(2, ACCENT, 0.9)
        arc(a - half, a + half)
        g.lineStyle(0)
        const p = point(a)
        const n = unitVector(Math.cos(a) / rx, Math.sin(a) / ry)
        const t = unitVector(-Math.sin(a) * rx, Math.cos(a) * ry)
        g.beginFill(ACCENT, 0.95)
        g.drawPolygon([
            p.x + n.x * 7,
            p.y + n.y * 7,
            p.x + n.x + t.x * 3.2,
            p.y + n.y + t.y * 3.2,
            p.x + n.x - t.x * 3.2,
            p.y + n.y - t.y * 3.2,
        ])
        g.endFill()
    }
}

/**
 * The glyph on a turn button: a turntable's rim seen from in front, flattened into an ellipse, swept
 * almost all the way round in the direction the button turns it. The sweep ends at the near side, so
 * its head points the way the button is named for. Centred on (0, 0).
 */
const turnGlyph = (direction: number): Graphics => {
    const g = new Graphics()
    const rx = 7.5
    const ry = 3.8
    // a = 90 degrees is the near side. Turning right carries the near side rightward, which is the
    // way of decreasing a: round the back from the front-right, ending just past the front.
    const at = (a: number) => ({ x: direction * rx * Math.cos(a), y: ry * Math.sin(a) })
    const from = (395 * Math.PI) / 180
    const to = (72 * Math.PI) / 180
    const steps = 28
    g.lineStyle(1.7, TEXT, 0.92)
    for (let i = 0; i <= steps; i++) {
        const p = at(from + ((to - from) * i) / steps)
        if (i === 0) {
            g.moveTo(p.x, p.y)
        } else {
            g.lineTo(p.x, p.y)
        }
    }
    g.lineStyle(0)
    // The head points along the way the sweep is travelling at its end.
    const end = at(to)
    const before = at(to + 0.05)
    const t = unitVector(end.x - before.x, end.y - before.y)
    g.beginFill(TEXT, 0.95)
    g.drawPolygon([
        end.x + t.x * 3.4,
        end.y + t.y * 3.4,
        end.x - t.x * 1.2 - t.y * 2.9,
        end.y - t.y * 1.2 + t.x * 2.9,
        end.x - t.x * 1.2 + t.y * 2.9,
        end.y - t.y * 1.2 - t.x * 2.9,
    ])
    g.endFill()
    return g
}

/** A round button that steps the figure one view round. Positioned by its centre. */
export const turnButton = (
    direction: number,
    cx: number,
    cy: number,
    onTurn: (direction: number) => void,
): UiButton => {
    const button = createIconButton({
        icon: "",
        theme: "ember",
        size: "small",
        shape: "circle",
        appearance: "outline",
        onClick: () => onTurn(direction),
    })
    button.name = direction > 0 ? "turn-right" : "turn-left"
    const glyph = turnGlyph(direction)
    button.addChild(glyph)
    // The button sizes itself when it is added (larger on a touch screen), so it is centred then.
    button.on("added", () => {
        const { width, height } = button.getBoxSize()
        button.position.set(Math.round(cx - width / 2), Math.round(cy - height / 2))
        glyph.position.set(width / 2, height / 2)
    })
    return button
}
